"use client";

import type { ProductCard as ProductCardData } from "@ellaandil/formatters";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { ProductCard, ProductCardSkeleton } from "@/components/ui/ProductCard";
import { Scrollbar } from "@/components/ui/Scrollbar";

const SKELETON_COUNT = 6;

/**
 * Smaller-card product carousel for inside the search modal. Wider slide counts
 * per snap than the shared CarouselTrack `quartets` layout (4) so cards visually
 * fit the dense modal layout from the Figma.
 */
export const ProductCarousel = ({
	products,
	loading,
}: {
	products: ProductCardData[];
	loading?: boolean;
}) => {
	const slides = loading && !products.length ? new Array(SKELETON_COUNT).fill(null) : products;
	const hasContent = slides.length > 0;

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: slides.length > 1,
		align: "start",
		containScroll: "trimSnaps",
		// Page through a whole view at a time on prev/next instead of one card.
		slidesToScroll: "auto",
	});

	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	useEffect(() => {
		if (!emblaApi) return;
		const sync = () => {
			setSelectedIndex(emblaApi.selectedScrollSnap());
			setSnapCount(emblaApi.scrollSnapList().length);
		};
		emblaApi.on("select", sync);
		emblaApi.on("reInit", sync);
		sync();
		return () => {
			emblaApi.off("select", sync);
			emblaApi.off("reInit", sync);
		};
	}, [emblaApi]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	if (!hasContent) return null;

	return (
		<div className="flex w-full flex-col gap-grid-section-vertical-md">
			<div ref={emblaRef} className="w-full overflow-hidden">
				<div className="flex items-start gap-grid-grid-gutter-md [&>*]:basis-1/2 [&>*]:shrink-0 [&>*]:grow-0 sm:[&>*]:basis-1/3 md:[&>*]:basis-1/4 lg:[&>*]:basis-1/5 xl:[&>*]:basis-1/6">
					{slides.map((product, i) =>
						product ? (
							<ProductCard key={product.id} product={product} size="small" showBrand={false} />
						) : (
							// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
							<ProductCardSkeleton key={i} size="small" />
						),
					)}
				</div>
			</div>
			{snapCount > 1 && (
				<Scrollbar
					current={selectedIndex + 1}
					total={snapCount}
					onPrev={scrollPrev}
					onNext={scrollNext}
				/>
			)}
		</div>
	);
};
